import React, { memo } from 'react';
import { Banner, MessageStatus } from '@nexus/lib-react/dist/core/Banner/Banner';
import { Paragraph2, Paragraph3 } from '@nexus/lib-react/dist/core/Typography';
import {
  Dac7Data,
  Dac7FormValidationResult,
  SellerBusinessDac7,
  SellerPrivateDac7,
  SellerSoleTraderDac7,
} from '../../../../../typings/dac7';
import DAC7ContentPrivate from './DAC7ContentPrivate';
import DAC7ContentBusiness from './DAC7ContentBusiness';
import DAC7ContentSoleTrader from './DAC7ContentSoleTrader';
import Dac7Layout from './components/Dac7Layout';
import Dac7CTABlock from './components/Dac7CTABlock';
import { useI18n } from '../../../../i18n';
import Dac7Input from './components/Dac7Input';

interface DAC7TabContentProps {
  dac7Data: NonNullable<Readonly<Dac7Data>>;
  validationResult: Dac7FormValidationResult;
  isDesktop: boolean;
  type: Dac7Data['type'];
}

const isDac7Private = (dac7Data: Readonly<Dac7Data>): dac7Data is SellerPrivateDac7 =>
  dac7Data.type === 'PRIVATE';
const isDac7Business = (dac7Data: Readonly<Dac7Data>): dac7Data is SellerBusinessDac7 =>
  dac7Data.type === 'BUSINESS';
const isDac7SoleTrader = (dac7Data: Readonly<Dac7Data>): dac7Data is SellerSoleTraderDac7 =>
  dac7Data.type === 'SOLE_TRADER';

interface TopBannerProps {
  isDesktop: boolean;
  validationResult: Dac7FormValidationResult;
  showWarningBanner: boolean;
  showBlockedBanner: boolean;
  deadline?: string;
}

function formatDeadline(deadline: string): string {
  const locale = 'pl-PL';
  const dateObj = new Date(deadline);
  const date = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long' }).format(dateObj);

  return date;
}

const TopBanner = memo(
  ({
    isDesktop,
    validationResult,
    showWarningBanner,
    showBlockedBanner,
    deadline,
  }: TopBannerProps) => {
    const i18n = useI18n();

    // form data is invalid, we need to show the error banner
    if (validationResult.length > 0) {
      const ErrorMessages = validationResult.map(([, translation]) => (
        <Paragraph2>
          <li>{`${i18n(translation)}`}</li>
        </Paragraph2>
      ));
      const message = (
        <>
          <Paragraph3>{i18n`dlv_dac7_top_banner_error_bold_text`}</Paragraph3>
          <Paragraph2>{i18n`dlv_dac7_top_banner_error_description`}</Paragraph2>
          {ErrorMessages}
        </>
      );
      return <Banner mt={8} message={message} type={MessageStatus.ERROR} noIcon={!isDesktop} />;
    }

    // Seller's payout account is not blocked yet as deadline hasn't passed. Show warning banner.
    if (showWarningBanner) {
      const message = (
        <>
          <Paragraph3>
            {i18n`dlv_dac7_top_banner_warning_bold_text`} {deadline}
          </Paragraph3>
          <Paragraph2>{i18n`dlv_dac7_top_banner_warning_description`}</Paragraph2>
        </>
      );
      return (
        <Banner mt={8} mb={8} message={message} type={MessageStatus.WARNING} noIcon={!isDesktop} />
      );
    }

    // Seller's payout account is blocked and the deadline to submit data has passed. Show blocked banner.
    if (showBlockedBanner) {
      const message = (
        <>
          <Paragraph3>{i18n`dlv_dac7_top_banner_blocked_bold_text`}</Paragraph3>
          <Paragraph2>{i18n`dlv_dac7_top_banner_blocked_description`}</Paragraph2>
        </>
      );
      return (
        <Banner mt={8} mb={8} message={message} type={MessageStatus.ERROR} noIcon={!isDesktop} />
      );
    }

    return <></>;
  },
);

const DAC7TabContent = ({ dac7Data, validationResult, isDesktop, type }: DAC7TabContentProps) => {
  const showWarningBanner = !!dac7Data.deadline && dac7Data.isBlockable;
  const showBlockedBanner = !!dac7Data.blockedAt;
  const formattedDeadline = formatDeadline(dac7Data?.deadline);

  return (
    <Dac7Layout
      isDesktop={isDesktop}
      topBanner={
        <TopBanner
          isDesktop={isDesktop}
          validationResult={validationResult}
          showWarningBanner={showWarningBanner}
          showBlockedBanner={showBlockedBanner}
          deadline={formattedDeadline}
        />
      }
    >
      <Dac7Input type="hidden" name="dac7_type" value={type} />
      {isDac7Private(dac7Data) ? <DAC7ContentPrivate data={dac7Data} /> : null}
      {isDac7Business(dac7Data) ? <DAC7ContentBusiness data={dac7Data} /> : null}
      {isDac7SoleTrader(dac7Data) ? <DAC7ContentSoleTrader data={dac7Data} /> : null}
      <Dac7CTABlock isDesktop={isDesktop} />
    </Dac7Layout>
  );
};

export default memo(DAC7TabContent);
